import React, { useState, useEffect, useRef, useCallback } from 'react';

const RealtimeSearch = ({ 
  onEventSelect, 
  placeholder = "Search events...", 
  className = "",
  showSuggestions = true,
  embedded = false 
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [totalCount, setTotalCount] = useState(0);
  const [queryTime, setQueryTime] = useState(0);
  
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/events/search/realtime`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchQuery,
          limit: embedded ? 8 : 15,
          include_suggestions: showSuggestions
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      
      setResults(data.events || []);
      setSuggestions(data.suggestions || []);
      setTotalCount(data.total_count || 0);
      setQueryTime(data.query_time_ms || 0);
      setIsOpen(true);
      setSelectedIndex(-1);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Real-time search error:', error);
        setResults([]);
        setSuggestions([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [embedded, showSuggestions]);

  // Handle input changes with debouncing
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      debouncedSearch(query);
    }, 300); // 300ms debounce

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, debouncedSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    const totalItems = results.length + suggestions.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < results.length) {
            // Selected an event
            const selectedEvent = results[selectedIndex];
            handleEventClick(selectedEvent);
          } else {
            // Selected a suggestion
            const suggestionIndex = selectedIndex - results.length;
            const selectedSuggestion = suggestions[suggestionIndex];
            handleSuggestionClick(selectedSuggestion);
          }
        }
        break;
        
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        searchRef.current?.blur();
        break;
    }
  };

  // Handle event selection
  const handleEventClick = (event) => {
    setIsOpen(false);
    setQuery('');
    if (onEventSelect) {
      onEventSelect(event);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.text);
    setIsOpen(false);
    // Trigger new search with suggestion
    setTimeout(() => debouncedSearch(suggestion.text), 100);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format date for display
  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();
    
    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get suggestion icon
  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'category': return '🏷️';
      case 'venue': return '📍';
      case 'host': return '👤';
      case 'location': return '🌎';
      case 'recent': return '🕒';
      default: return '🔍';
    }
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-4 py-3 pl-12 pr-4 text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
        />
        
        {/* Search Icon */}
        <div className="absolute inset-y-0 left-0 flex items-center pl-4">
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
          ) : (
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>

        {/* Clear Button */}
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setIsOpen(false);
              searchRef.current?.focus();
            }}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && (results.length > 0 || suggestions.length > 0) && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto"
        >
          {/* Search Stats */}
          {totalCount > 0 && (
            <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
              Found {totalCount} events in {queryTime}ms
            </div>
          )}

          {/* Search Results */}
          {results.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Events
              </div>
              {results.map((event, index) => (
                <button
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 flex items-start space-x-3 ${
                    selectedIndex === index ? 'bg-purple-50 border-r-2 border-purple-500' : ''
                  }`}
                >
                  {/* Event Image or Placeholder */}
                  <div className="flex-shrink-0">
                    {event.featured_image_url ? (
                      <img 
                        src={event.featured_image_url} 
                        alt={event.title}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center">
                        <span className="text-white text-lg font-bold">
                          {event.title.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Event Details */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {event.title}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center space-x-4">
                      <span>{formatEventDate(event.start_at)}</span>
                      {event.venue_name && (
                        <span className="flex items-center">
                          📍 {event.venue_name}
                        </span>
                      )}
                      {event.is_free ? (
                        <span className="text-green-600 font-medium">Free</span>
                      ) : event.price_min && (
                        <span className="text-purple-600 font-medium">
                          ${event.price_min}
                        </span>
                      )}
                    </div>
                    {event.host_name && (
                      <div className="text-xs text-gray-400">
                        by {event.host_name}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Search Suggestions */}
          {suggestions.length > 0 && (
            <div className="py-2 border-t border-gray-100">
              <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Suggestions
              </div>
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 flex items-center space-x-3 ${
                    selectedIndex === results.length + index ? 'bg-purple-50 border-r-2 border-purple-500' : ''
                  }`}
                >
                  <span className="text-lg">{getSuggestionIcon(suggestion.type)}</span>
                  <div className="flex-1">
                    <span className="text-gray-900">{suggestion.text}</span>
                    {suggestion.count && (
                      <span className="ml-2 text-sm text-gray-500">
                        ({suggestion.count} events)
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 capitalize">
                    {suggestion.type}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {results.length === 0 && suggestions.length === 0 && query.length >= 2 && !isLoading && (
            <div className="px-4 py-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.44-.816-6.114-2.176M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-lg font-medium mb-1">No events found</p>
              <p className="text-sm">Try adjusting your search terms</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RealtimeSearch;